/**
 * Validates if the file is an xlsx file
 * @param {File} file - File object to validate
 * @returns {boolean} - True if valid xlsx file
 */
export const isValidXlsxFile = (file) => {
  return file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
}

/**
 * Reads and parses an xlsx file
 * @param {File} file - File object to read
 * @returns {Promise<Array>} - Parsed rows from the file
 */
export const parseXlsxFile = async (file) => {
  // Loaded on first import: xlsx is ~0.3 MB and only the client import needs it
  const [arrayBuffer, XLSX] = await Promise.all([file.arrayBuffer(), import("xlsx")])
  const workbook = XLSX.read(arrayBuffer, { type: "array" })
  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  return XLSX.utils.sheet_to_json(sheet)
}

/**
 * Validates file content
 * @param {Array} rows - Parsed rows from file
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateFileContent = (rows) => {
  if (rows.length === 0) {
    return {
      isValid: false,
      error: "Uploaded file is empty."
    }
  }

  if (rows.length > 500) {
    return {
      isValid: false,
      error: "The file exceeds the 500-record limit. Please upload a file containing 500 records or fewer."
    }
  }

  return { isValid: true, error: null }
}

/**
 * Creates a file object for state management
 * @param {File} file - Original file object
 * @returns {Object} - Formatted file object
 */
export const createFileObject = (file) => ({
  uid: file.uid || Date.now().toString(),
  name: file.name,
  status: "uploading",
  originFileObj: file
})
